/**
 * Money.ts — SKCATALOG-CC-002 CR-3 / CR-4.
 *
 * The API sends money as `{ amount: "<minor units>", currency }` with the amount
 * a STRING. Every render and every parse goes through here, for two reasons:
 *
 *  1. `Number(m.amount)` in a component is the bug the CR exists to remove. A
 *     price beyond 2^53 minor units loses its last digit silently, and a
 *     component that does float arithmetic on a price eventually disagrees with
 *     Billing by a paisa. The formatting below keeps the integer arithmetic in
 *     string/BigInt space.
 *  2. Amounts render mono and LTR per the suite spec (SKCATALOG-DB-001), which
 *     is a presentation rule that belongs in one place, not in each screen.
 */

import type { Money } from '../schemas/catalog/common';

/** Minor units per major unit, by currency. Explicit rather than assumed, so a
 *  zero-decimal currency can be added in exactly one place. */
const MINOR_DIGITS: Record<string, number> = { INR: 2, AED: 2, USD: 2, EUR: 2, GBP: 2 };

const SYMBOLS: Record<string, string> = { INR: '₹', AED: 'AED ', USD: '$', EUR: '€', GBP: '£' };

function digitsOf(currency: string): number {
  return MINOR_DIGITS[(currency || 'INR').toUpperCase()] ?? 2;
}

/** True for the wire money shape. Guards against a legacy numeric payload. */
export function isMoney(v: unknown): v is Money {
  return (
    !!v &&
    typeof v === 'object' &&
    typeof (v as Money).amount === 'string' &&
    typeof (v as Money).currency === 'string'
  );
}

/**
 * Split a minor-unit STRING into its major and fractional halves without going
 * through a float. Pure string surgery, so an amount of any length survives.
 */
function splitMinor(amount: string, digits: number): { neg: boolean; major: string; frac: string } {
  const neg = amount.startsWith('-');
  const raw = (neg ? amount.slice(1) : amount).replace(/[^0-9]/g, '') || '0';
  if (digits === 0) return { neg, major: raw, frac: '' };
  const padded = raw.padStart(digits + 1, '0');
  return {
    neg,
    major: padded.slice(0, padded.length - digits),
    frac: padded.slice(padded.length - digits),
  };
}

/** Indian digit grouping (2,2,3 from the right) — ₹12,34,567.89, not ₹1,234,567.89. */
function groupIndian(major: string): string {
  if (major.length <= 3) return major;
  const head = major.slice(0, major.length - 3);
  const tail = major.slice(major.length - 3);
  return head.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + tail;
}

function groupWestern(major: string): string {
  return major.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Display a money value: `₹4,500.00`.
 *
 * `dash` is what comes back for a null/absent price — a missing price and a free
 * item are different facts, so the default is an em dash rather than ₹0.
 */
export function formatMoney(m: Money | null | undefined, dash = '—'): string {
  if (!isMoney(m)) return dash;
  const currency = m.currency.toUpperCase();
  const digits = digitsOf(currency);
  const { neg, major, frac } = splitMinor(m.amount, digits);
  const grouped = currency === 'INR' ? groupIndian(major) : groupWestern(major);
  const symbol = SYMBOLS[currency] ?? `${currency} `;
  return `${neg ? '-' : ''}${symbol}${grouped}${digits > 0 ? `.${frac}` : ''}`;
}

/**
 * The MAJOR-unit string to put in an editable price input — `"4500.00"`. No
 * grouping and no symbol, so it round-trips through `parseMajorToMinor` exactly.
 */
export function moneyToInput(m: Money | null | undefined): string {
  if (!isMoney(m)) return '';
  const digits = digitsOf(m.currency);
  const { neg, major, frac } = splitMinor(m.amount, digits);
  return `${neg ? '-' : ''}${major}${digits > 0 ? `.${frac}` : ''}`;
}

/**
 * Parse what a human typed (major units — rupees) into MINOR units.
 *
 * Returns null for anything unparseable, so a caller can refuse to submit
 * instead of sending 0 — silently pricing something at zero is worse than a
 * validation message.
 */
export function parseMajorToMinor(input: string, currency = 'INR'): number | null {
  const cleaned = (input ?? '').replace(/[,\s₹]/g, '');
  if (cleaned === '' || !/^-?\d*(\.\d*)?$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  const factor = 10 ** digitsOf(currency);
  return Math.round(n * factor);
}

/** A bare minor-unit number (e.g. a legacy field value already in paise) → Money. */
export function moneyFromMinor(minor: number | string, currency = 'INR'): Money {
  const n = typeof minor === 'string' ? Number(minor) : minor;
  return { amount: String(Math.round(Number.isFinite(n) ? n : 0)), currency: currency.toUpperCase() };
}

/** A MAJOR-unit number (a legacy `base_price` field value in rupees) → Money. */
export function moneyFromMajor(major: number | string, currency = 'INR'): Money {
  const n = typeof major === 'string' ? Number(major) : major;
  const factor = 10 ** digitsOf(currency);
  return {
    amount: String(Math.round((Number.isFinite(n) ? n : 0) * factor)),
    currency: currency.toUpperCase(),
  };
}

/**
 * Coerce whatever a CURRENCY field value turns out to be into Money.
 *
 * The API now sends the money shape, but a field value can still arrive as a
 * bare MAJOR-unit number from an older payload or from the raw editor view
 * (`GET /items/:id` returns `valueJson` verbatim). Handling both here means no
 * screen has to guess.
 */
export function asMoney(value: unknown, currency = 'INR'): Money | null {
  if (value === null || value === undefined || value === '') return null;
  if (isMoney(value)) return value;
  if (typeof value === 'number') return moneyFromMajor(value, currency);
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? moneyFromMajor(n, currency) : null;
  }
  return null;
}
